// Import Dependencies
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import React, { useState, useMemo, Fragment, useRef, useEffect, isValidElement, cloneElement } from "react";
import { createPortal } from "react-dom";

// Local Imports
import { Table, Card, THead, TBody, Th, Tr, Td, TableSkeleton } from "components/ui";
import { TableSortIcon } from "components/shared/table/TableSortIcon";
import { useLockScrollbar, useDidUpdate, useUserPreference, PREFERENCE_TYPES } from "hooks";
import { fuzzyFilter } from "utils/react-table/fuzzyFilter";
import { useSkipper } from "utils/react-table/useSkipper";
import { Toolbar } from "./Toolbar";
import { PaginationSection } from "components/shared/table/PaginationSection";
import { SelectedRowsActions } from "./SelectedRowsActions";
import { useThemeContext } from "app/contexts/theme/context";
import { getUserAgentBrowser } from "utils/dom/getUserAgentBrowser";
import { useMediaQuery } from "hooks/useMediaQuery";
import { triggerRowContextMenu, GlobalTableContextMenu } from "custom-components/shared/TableActionMenu";

// ----------------------------------------------------------------------

const isSafari = getUserAgentBrowser() === "Safari";

function extractSizeFromClass(className) {
  if (!className) return null;
  const pctMatch = className.match(/w-\[(\d+(?:\.\d+)?)%\]/);
  if (pctMatch) {
    const pct = parseFloat(pctMatch[1]);
    return Math.round((pct / 100) * 1200);
  }
  const pxMatch = className.match(/w-\[(\d+)px\]/);
  if (pxMatch) {
    return parseInt(pxMatch[1], 10);
  }
  const twMatch = className.match(/\bw-(\d+)\b/);
  if (twMatch) {
    return parseInt(twMatch[1], 10) * 4;
  }
  const fracMatch = className.match(/\bw-(\d+)\/(\d+)\b/);
  if (fracMatch) {
    const num = parseInt(fracMatch[1], 10);
    const den = parseInt(fracMatch[2], 10);
    return Math.round((num / den) * 1200);
  }
  return null;
}

function calculateHeaderMinSize(col) {
  if (col.minWidth || col.minSize) return col.minWidth || col.minSize;
  const colId = col.key || col.id || col.accessorKey;
  if (colId === "select") return 48;

  let headerText = "";
  if (typeof col.header === "string") {
    headerText = col.header;
  } else if (typeof col.label === "string") {
    headerText = col.label;
  } else if (typeof col.DisplayName === "string") {
    headerText = col.DisplayName;
  }

  if (colId === "actions" || colId === "Actions") {
    // header text must fit, otherwise it overflows the cell and the header bg is cut off
    const estimated = headerText ? Math.ceil(headerText.length * 8.5) + 52 : 0;
    return Math.max(90, estimated);
  }

  if (headerText) {
    const estimated = Math.ceil(headerText.length * 8.5) + 52;
    return Math.max(130, estimated);
  }

  return 140;
}

export default function CustomTable({
  columns: simpleColumns,
  data,
  title = "",
  subtitle = "",
  titleIcon = null,
  storageKey = "custom-table",
  pageKey,
  showToolbar = true,
  showPagination = true,
  showSearch = true,
  showStatusFilter = true,
  showActionButtons = true,
  showColumnConfig = true,
  onRowClick = () => { },
  onRowContextMenu,
  selectable = false,
  renderSelectedActions,
  renderTopActions,
  renderBelowToolbar,
  getRowClassName,
  getRowTitle,
  onPrint,
  onExportPDF,
  onExportCSV,
  renderDetailRow,
  expandOnRowClick = true,
  loading = false,
  // Server-side pagination & sorting props
  pagination,
  onPageChange,
  onLimitChange,
  sortBy,
  sortOrder,
  onSort,
  // Selection Props
  rowSelection: propRowSelection,
  onRowSelectionChange: propOnRowSelectionChange,
  getRowId,
  preserveSelection = false,
  // Visibility Props
  columnVisibility: propColumnVisibility,
  onColumnVisibilityChange: propOnColumnVisibilityChange,
  // Search/Filter Props
  globalFilter: propGlobalFilter,
  onGlobalFilterChange: propOnGlobalFilterChange,
  manualFiltering,
  noDataMessage = "No data to show",
  noDataSubtitle,
  emptyStateAction,
  headerActions,
  searchPlaceholder,
  compactPagination = false,
  dense,
  colDense,
  persistSettings = false,
  // Row virtualization - windows the rendered rows so large page sizes (500-1000 rows)
  // don't balloon the DOM. On by default; escape hatch for screens that hit issues.
  enableVirtualization = true,
  virtualizationThreshold = 30,
  enableFullScreen: propEnableFullScreen,
  onFullScreenChange,
  disableToolbarPortal = false,
}) {
  const { cardSkin } = useThemeContext();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const effectivePageKey = pageKey || (storageKey ? storageKey.replace(/-/g, "_") : null);

  // Backend user preferences (TenantUserPreferences API)
  const { preferenceState: apiVisibility, savePreference: saveApiVisibility } = useUserPreference({
    pageKey: effectivePageKey,
    typeKey: PREFERENCE_TYPES.COLUMN_VISIBILITY,
    defaultState: {},
  });

  const { preferenceState: apiLayout, savePreference: saveApiLayout, isPreferenceLoading: isLayoutLoading } = useUserPreference({
    pageKey: effectivePageKey,
    typeKey: PREFERENCE_TYPES.COLUMN_LAYOUT,
    defaultState: { columnSizing: {}, columnOrder: [] },
  });

  const { preferenceState: apiSettings, savePreference: saveApiSettings } = useUserPreference({
    pageKey: effectivePageKey,
    typeKey: "TableSettings",
    defaultState: {
      enableFullScreen: false,
      enableRowDense: false,
      enableColDense: false,
      enableSorting: true,
      enableColumnFilters: true,
    },
  });

  const [tableSettingsState, setTableSettingsState] = useState({
    enableFullScreen: false,
    enableRowDense: false,
    enableColDense: false,
    enableSorting: true,
    enableColumnFilters: true,
  });

  const tableSettings = effectivePageKey && apiSettings ? apiSettings : tableSettingsState;
  const setTableSettings = (updater) => {
    const nextSettings = typeof updater === "function" ? updater(tableSettings) : updater;
    if (effectivePageKey) {
      saveApiSettings(nextSettings);
    } else {
      setTableSettingsState(nextSettings);
    }
  };

  const activeEnableFullScreen = propEnableFullScreen !== undefined ? propEnableFullScreen : tableSettings.enableFullScreen;

  const [localGlobalFilter, setLocalGlobalFilter] = useState("");
  const activeGlobalFilter = propGlobalFilter !== undefined ? propGlobalFilter : localGlobalFilter;
  const activeOnGlobalFilterChange = propOnGlobalFilterChange !== undefined ? propOnGlobalFilterChange : setLocalGlobalFilter;
  const [sorting, setSorting] = useState([]);
  const [localRowSelection, setLocalRowSelection] = useState({});
  const activeRowSelection = propRowSelection !== undefined ? propRowSelection : localRowSelection;
  const activeOnRowSelectionChange = propOnRowSelectionChange !== undefined ? propOnRowSelectionChange : setLocalRowSelection;
  const [expanded, setExpanded] = useState({});

  const [localColumnSizing, setLocalColumnSizing] = useState({});
  const columnSizing = effectivePageKey && apiLayout?.columnSizing ? apiLayout.columnSizing : localColumnSizing;

  const setColumnSizing = (updater) => {
    const nextSizing = typeof updater === "function" ? updater(columnSizing) : updater;
    if (effectivePageKey) {
      saveApiLayout({ ...apiLayout, columnSizing: nextSizing });
    } else {
      setLocalColumnSizing(nextSizing);
    }
  };

  // Server-side manual pagination settings
  const isServerSide = !!pagination;

  const handlePaginationChange = (updater) => {
    if (typeof updater === "function") {
      const nextState = updater(
        isServerSide
          ? { pageIndex: pagination.page - 1, pageSize: pagination.limit }
          : table.getState().pagination
      );
      if (nextState.pageIndex !== pagination.page - 1 && onPageChange) {
        onPageChange(nextState.pageIndex + 1);
      }
      if (nextState.pageSize !== pagination.limit && onLimitChange) {
        onLimitChange(nextState.pageSize);
      }
    } else {
      if (updater.pageIndex !== pagination.page - 1 && onPageChange) {
        onPageChange(updater.pageIndex + 1);
      }
      if (updater.pageSize !== pagination.limit && onLimitChange) {
        onLimitChange(updater.pageSize);
      }
    }
  };

  const lastKnownTotalRef = useRef(0);
  if (pagination?.total !== undefined && pagination?.total !== null) {
    lastKnownTotalRef.current = pagination.total;
  }
  const currentTotal = (pagination?.total !== undefined && pagination?.total !== null) ? pagination.total : lastKnownTotalRef.current;
  const pageCount = isServerSide ? Math.ceil((currentTotal || 0) / (pagination.limit || 10)) : undefined;

  // Server-side manual sorting settings
  const isServerSideSorting = !!onSort;
  const sortingState = useMemo(() => {
    if (sortBy) {
      return [{ id: sortBy, desc: String(sortOrder).toLowerCase() === "desc" }];
    }
    return [];
  }, [sortBy, sortOrder]);

  const handleSortingChange = (updater) => {
    let nextSorting = [];
    if (typeof updater === "function") {
      nextSorting = updater(sortingState);
    } else {
      nextSorting = updater;
    }

    if (onSort && nextSorting.length > 0) {
      const sortField = nextSorting[0].id;
      const sortDir = nextSorting[0].desc ? "desc" : "asc";
      onSort(sortField, sortDir);
    } else if (onSort) {
      onSort("", "");
    }
  };

  const currentSorting = isServerSideSorting ? sortingState : sorting;
  const handleSortingChangeValue = isServerSideSorting ? handleSortingChange : setSorting;

  const [localColumnVisibilityState, setLocalColumnVisibilityState] = useState({});
  const localColumnVisibility = effectivePageKey && apiVisibility ? apiVisibility : localColumnVisibilityState;
  const setLocalColumnVisibility = (updater) => {
    const nextVis = typeof updater === "function" ? updater(localColumnVisibility) : updater;
    if (effectivePageKey) {
      saveApiVisibility(nextVis);
    } else {
      setLocalColumnVisibilityState(nextVis);
    }
  };

  const columnVisibility = propColumnVisibility !== undefined ? propColumnVisibility : localColumnVisibility;
  const handleColumnVisibilityChange = propOnColumnVisibilityChange !== undefined ? propOnColumnVisibilityChange : setLocalColumnVisibility;

  const [columnPinningState, setColumnPinningState] = useState({});
  const columnPinning = columnPinningState;
  const setColumnPinning = setColumnPinningState;

  const [localColumnOrderState, setLocalColumnOrderState] = useState([]);
  const columnOrder = effectivePageKey && apiLayout?.columnOrder ? apiLayout.columnOrder : localColumnOrderState;
  const setColumnOrder = (updater) => {
    const nextOrder = typeof updater === "function" ? updater(columnOrder) : updater;
    if (effectivePageKey) {
      saveApiLayout({ ...apiLayout, columnOrder: nextOrder });
    } else {
      setLocalColumnOrderState(nextOrder);
    }
  };

  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [hoveredResizerId, setHoveredResizerId] = useState(null);

  const handleDragStart = (e, columnId) => {
    if (columnId === "select" || columnId === "actions") {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("text/plain", columnId);
    setDraggedColumnId(columnId);
  };

  const handleDragOver = (e, columnId) => {
    if (columnId === "select" || columnId === "actions") {
      return;
    }
    e.preventDefault();
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    const sourceColumnId = e.dataTransfer.getData("text/plain") || draggedColumnId;
    if (!sourceColumnId || sourceColumnId === targetColumnId) return;

    const newOrder = [...effectiveColumnOrder];
    const sourceIndex = newOrder.indexOf(sourceColumnId);
    const targetIndex = newOrder.indexOf(targetColumnId);

    if (sourceIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, sourceColumnId);
      setColumnOrder(newOrder);
    }
    setDraggedColumnId(null);
  };

  const handleDragEnd = () => {
    setDraggedColumnId(null);
  };

  const columns = useMemo(() => {
    const processColumn = (col) => {
      const colId = col.key || col.id || col.accessorKey;
      const extractedSize = extractSizeFromClass(col.className) || extractSizeFromClass(col.headerClassName);
      const computedMinSize = col.minWidth || col.minSize || calculateHeaderMinSize(col);
      const initialSize = col.width || col.size || extractedSize || (colId === "select" ? 50 : colId === "actions" ? 100 : 180);
      const computedSize = Math.max(initialSize, computedMinSize);
      const colDef = {
        id: colId,
        // accessorFn takes precedence when provided -- a column computing its value from a nested
        // path (e.g. row.option?.category) has no single top-level key to read via accessorKey, and
        // silently setting accessorKey here as well as before left accessorFn columns with no way
        // to derive a value at all, so getValue() always returned undefined for them.
        ...(col.accessorFn ? { accessorFn: col.accessorFn } : { accessorKey: col.accessorKey || col.key }),
        header: col.header,
        label: col.label || col.header,
        cell: (info) => {
          if (col.render) {
            return col.render(info.row, info.getValue, info.table, info);
          }
          if (col.cell) {
            return typeof col.cell === "function" ? col.cell(info) : col.cell;
          }
          const val = info.getValue ? info.getValue() : undefined;
          if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
            const [y, m, d] = val.split("-");
            return `${m}/${d}/${y}`;
          }
          return val;
        },
        enableSorting: col.sortable !== false,
        sortingFn: col.sortingFn || 'auto',
        enableHiding: !col.sticky,
        size: computedSize,
        minSize: computedMinSize,
        maxSize: col.maxWidth || col.maxSize,
        enableResizing: colId !== "select" && colId !== "actions" && col.resizable !== false,
        meta: {
          className: col.className,
          headerClassName: col.headerClassName,
          sticky: col.sticky,
          alwaysVisible: !!col.sticky,
          isDefault: col.isDefault !== undefined ? col.isDefault : true,
          isLast: col.meta?.isLast,
        },
      };

      if (Array.isArray(col.columns) && col.columns.length > 0) {
        colDef.columns = col.columns.map(processColumn);
      }

      return colDef;
    };

    return simpleColumns.map(processColumn);
  }, [simpleColumns]);

  const effectiveColumnOrder = useMemo(() => {
    if (!columns || columns.length === 0) return [];
    const currentIds = columns.map((c) => c.id).filter(Boolean);
    let baseOrder = Array.isArray(columnOrder) && columnOrder.length > 0 ? [...columnOrder] : [];
    let filtered = baseOrder.filter((id) => currentIds.includes(id));

    currentIds.forEach((id) => {
      if (!filtered.includes(id)) {
        const origIdx = currentIds.indexOf(id);
        if (origIdx > 0) {
          const prevId = currentIds[origIdx - 1];
          const prevPos = filtered.indexOf(prevId);
          if (prevPos !== -1) {
            filtered.splice(prevPos + 1, 0, id);
            return;
          }
        }
        filtered.push(id);
      }
    });

    const lastColumns = columns.filter(c => c.meta?.isLast).map(c => c.id);
    lastColumns.forEach((lastColId) => {
      const idx = filtered.indexOf(lastColId);
      if (idx !== -1 && idx !== filtered.length - 1) {
        filtered.splice(idx, 1);
        filtered.push(lastColId);
      }
    });

    const selectIdx = filtered.findIndex((id) => id.toLowerCase() === "select");
    if (selectIdx !== -1 && selectIdx !== 0) {
      const [selectId] = filtered.splice(selectIdx, 1);
      filtered.unshift(selectId);
    }

    const actionsIdx = filtered.findIndex((id) => id.toLowerCase() === "actions" || id.toLowerCase() === "action");
    if (actionsIdx !== -1 && actionsIdx !== filtered.length - 1) {
      const [actionsId] = filtered.splice(actionsIdx, 1);
      filtered.push(actionsId);
    }

    return filtered;
  }, [columns, columnOrder]);

  // On mobile, disable all column pinning so the table scrolls freely.
  // On desktop, always keep sticky columns pinned first on the left.
  const effectiveColumnPinning = useMemo(() => {
    if (isMobile) return {};
    const stickyIds = simpleColumns
      .filter((col) => col.sticky)
      .map((col) => col.key)
      .filter(Boolean);
    if (stickyIds.length === 0) return columnPinning;
    const currentLeft = columnPinning.left || [];
    const nonSticky = currentLeft.filter((id) => !stickyIds.includes(id));
    const combinedLeft = [...stickyIds, ...nonSticky];

    const activeOrder = effectiveColumnOrder;
    const sortedLeft = combinedLeft.sort((a, b) => {
      if (a === "select") return -1;
      if (b === "select") return 1;
      const idxA = activeOrder.indexOf(a);
      const idxB = activeOrder.indexOf(b);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });

    return { ...columnPinning, left: sortedLeft };
  }, [columnPinning, simpleColumns, isMobile, effectiveColumnOrder]);

  // Sync columns with stored order to handle newly added or removed columns dynamically
  useEffect(() => {
    // Avoid wiping persisted columnOrder on initial render while columns are loading or incomplete
    if (columns.length > 1 && !loading && !isLayoutLoading) {
      const isDifferent = effectiveColumnOrder.length !== columnOrder.length ||
        effectiveColumnOrder.some((val, index) => val !== columnOrder[index]);

      if (isDifferent) {
        setColumnOrder(effectiveColumnOrder);
      }
    }
  }, [columns, columnOrder, effectiveColumnOrder, setColumnOrder, loading, isLayoutLoading]);

  const [autoResetPageIndex] = useSkipper();

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: "onChange",
    getRowId: getRowId || ((row, index) => row.id?.toString() || row.LeadId?.toString() || index.toString()),
    initialState: {
      pagination: {
        pageSize: 100,
        pageIndex: 0,
      },
    },
    state: {
      globalFilter: activeGlobalFilter,
      sorting: currentSorting,
      columnVisibility,
      columnPinning: effectiveColumnPinning,
      tableSettings: { ...tableSettings, enableFullScreen: activeEnableFullScreen },
      rowSelection: activeRowSelection,
      expanded,
      columnOrder: effectiveColumnOrder,
      columnSizing,
      ...(isServerSide && {
        pagination: {
          pageIndex: pagination.page - 1,
          pageSize: pagination.limit,
        },
      }),
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    meta: {
      setTableSettings: (updater) => {
        setTableSettings((prev) => {
          const next = typeof updater === "function" ? updater(prev) : updater;
          if (propEnableFullScreen !== undefined && next.enableFullScreen !== activeEnableFullScreen) {
            if (onFullScreenChange) onFullScreenChange(next.enableFullScreen);
          }
          return next;
        });
      },
      tableSettings: { ...tableSettings, enableFullScreen: activeEnableFullScreen },
      totalCount: isServerSide ? currentTotal : undefined,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
      progressFilter: (row, columnId, value) => {
        const progress = row.getValue(columnId);
        switch (value) {
          case "pending":
            return progress === 0;
          case "in-progress":
            return progress > 0 && progress < 100;
          case "done":
            return progress === 100;
          default:
            return true;
        }
      },
    },
    manualPagination: isServerSide,
    pageCount: isServerSide ? pageCount : undefined,
    onPaginationChange: isServerSide ? handlePaginationChange : undefined,
    manualSorting: isServerSideSorting,
    manualFiltering: manualFiltering !== undefined ? manualFiltering : isServerSide,
    enableSorting: tableSettings.enableSorting,
    enableColumnFilters: tableSettings.enableColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: activeOnGlobalFilterChange,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: fuzzyFilter,
    onSortingChange: handleSortingChangeValue,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnPinningChange: setColumnPinning,
    autoResetPageIndex: isServerSide ? false : autoResetPageIndex,
    enableRowSelection: selectable ? (row) => !row.original.isSeparator : false,
    onRowSelectionChange: activeOnRowSelectionChange,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
  });

  useDidUpdate(() => {
    if (!preserveSelection) {
      table.resetRowSelection();
    }
  }, [data]);
  useLockScrollbar(false); // Disabled because full screen now just hides the header
  const tableRef = useRef(null);

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [pageIndex, pageSize]);

  const rows = table.getRowModel().rows;
  const totalVisibleColumns = table.getVisibleFlatColumns().length;

  // Flatten data rows + any expanded renderDetailRow content into one list so the
  // virtualizer can window across both uniform rows and variable-height detail rows.
  const flatItems = useMemo(() => {
    const items = [];
    for (const row of rows) {
      items.push({ type: "row", row, id: row.id });
      if (row.getIsExpanded() && renderDetailRow) {
        items.push({ type: "detail", row, id: `${row.id}__detail` });
      }
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, expanded, renderDetailRow]);

  const shouldVirtualize = enableVirtualization && rows.length > virtualizationThreshold;

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? flatItems.length : 0,
    getScrollElement: () => tableRef.current,
    estimateSize: () => (tableSettings.enableRowDense ? 36 : 48),
    measureElement: (el) => el?.getBoundingClientRect().height,
    overscan: 8,
    getItemKey: (index) => flatItems[index]?.id ?? index,
  });

  const virtualItems = shouldVirtualize ? rowVirtualizer.getVirtualItems() : [];

  const activeSeparatorItem = useMemo(() => {
    if (!shouldVirtualize || virtualItems.length === 0) return null;
    const firstVisibleIndex = virtualItems[0].index;
    let activeSep = null;
    for (let i = 0; i < flatItems.length; i++) {
      if (i > firstVisibleIndex) break;
      if (flatItems[i].type === "row" && flatItems[i].row.original.isSeparator) {
        activeSep = flatItems[i];
      }
    }
    // If the active separator is already in virtualItems, we don't need to duplicate it
    if (activeSep && virtualItems.some(v => v.index === flatItems.indexOf(activeSep))) {
      return null;
    }
    return activeSep;
  }, [virtualItems, flatItems, shouldVirtualize]);

  const virtualPaddingStart = virtualItems.length > 0 ? virtualItems[0].start : 0;
  // Adjust padding to account for the manually rendered active separator
  const adjustedPaddingStart = Math.max(0, virtualPaddingStart - (activeSeparatorItem ? 38 : 0));

  const virtualPaddingEnd = virtualItems.length > 0
    ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;

  const hasResizedColumns = Object.keys(table.getState().columnSizing).length > 0;
  const hasExplicitWidths = useMemo(() => {
    return simpleColumns.some(
      (col) => col.width !== undefined || col.size !== undefined || extractSizeFromClass(col.className) || extractSizeFromClass(col.headerClassName)
    );
  }, [simpleColumns]);

  const useFixedLayout = hasResizedColumns || hasExplicitWidths;

  const getPinnedStyles = (column, isHeader = false) => {
    const isPinned = column.getIsPinned();
    if (!isPinned) return {};

    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

    if (isPinned === 'left') {
      const offset = column.getStart('left');
      const value = `${offset}px`;
      return {
        position: 'sticky',
        left: isRtl ? undefined : value,
        right: isRtl ? value : undefined,
        top: isHeader ? 0 : undefined,
        zIndex: isHeader ? 40 : 20,
      };
    }

    if (isPinned === 'right') {
      const offset = column.getAfter('right');
      const value = `${offset}px`;
      return {
        position: 'sticky',
        right: isRtl ? undefined : value,
        left: isRtl ? value : undefined,
        top: isHeader ? 0 : undefined,
        zIndex: isHeader ? 35 : 10,
      };
    }

    return {};
  };

  const renderDataRow = (row, { ref, dataIndex } = {}) => {
    if (row.original.isSeparator) {
      if (row.original.isPhaseSeparator) {
        const item = row.original;
        return (
          <Tr
            key={row.id}
            ref={ref}
            onContextMenu={(e) => {
              if (onRowContextMenu) {
                if (window.innerWidth >= 768) {
                  e.preventDefault();
                  onRowContextMenu(e, item);
                }
              }
            }}
            className={clsx(
              "sticky z-[14] select-none border-y border-indigo-200/80 dark:border-indigo-900/60 bg-gradient-to-r from-indigo-50/90 via-slate-50 to-white dark:from-indigo-950/40 dark:via-dark-850 dark:to-dark-800 cursor-default",
              tableSettings.enableRowDense ? "top-[24px]" : "top-[39px]"
            )}
          >
            <Td colSpan={totalVisibleColumns} className="!p-0 border-l-4 border-l-indigo-500 dark:border-l-indigo-400">
              <div className="flex items-center justify-between px-3.5 py-2 w-full gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex items-center justify-center w-5 h-5 rounded bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 font-bold text-xs shrink-0">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </span>
                  <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
                    <span className="text-xs font-bold tracking-wide text-slate-800 dark:text-dark-100 uppercase truncate">
                      {item.separatorLabel}
                    </span>
                    {item.milestoneName && (
                      <span className="text-[10px] text-gray-500 dark:text-dark-400 font-medium whitespace-nowrap">
                        (in {item.milestoneName})
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {item.totalCount !== undefined && (
                    <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-600 dark:text-dark-300">
                      <span>{item.completedCount}/{item.totalCount} completed</span>
                      <div className="w-16 bg-gray-200 dark:bg-dark-700 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={clsx(
                            "h-full transition-all duration-300",
                            item.isPhaseComplete ? "bg-emerald-500" : "bg-indigo-500"
                          )}
                          style={{ width: `${item.progressPercent || 0}%` }}
                        />
                      </div>
                      <span className="font-bold text-[10px] min-w-[28px] text-right">
                        {item.progressPercent || 0}%
                      </span>
                    </div>
                  )}

                  <span
                    className={clsx(
                      "px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border",
                      item.isPhaseComplete
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/40"
                        : (item.progressPercent || 0) > 0
                        ? "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-900/40"
                        : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-dark-700 dark:text-dark-300 dark:border-dark-600"
                    )}
                  >
                    {item.isPhaseComplete ? "Phase Completed" : (item.progressPercent || 0) > 0 ? "In Progress" : "Pending"}
                  </span>

                  {item.actions && (
                    <div className="flex items-center gap-1.5 ml-1">
                      {item.actions}
                    </div>
                  )}
                </div>
              </div>
            </Td>
          </Tr>
        );
      }

      return (
        <Tr
          key={row.id}
          ref={ref}
          style={{ zIndex: 25 }}
          className={clsx(
            "sticky z-[25] select-none border-b border-gray-200 dark:border-dark-650 bg-primary dark:bg-primary-600 cursor-default hover:!bg-primary dark:hover:!bg-primary-600",
            tableSettings.enableRowDense ? "top-[24px]" : "top-[39px]"
          )}
        >
          <Td
            colSpan={totalVisibleColumns}
            style={{ zIndex: 25 }}
            className="!p-0 !bg-primary dark:!bg-primary-600 hover:!bg-primary dark:hover:!bg-primary-600 group-hover/tr:!bg-primary dark:group-hover/tr:!bg-primary-600 relative z-[25]"
          >
            <div className="sticky left-0 w-max flex items-center gap-2.5 px-3 py-2 z-[26]">
              {/* Left accent bar */}
              <span className="w-1.5 h-4 rounded-full shrink-0 bg-white dark:bg-white" />
              {/* Dot */}
              <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-white dark:bg-white" />
              {/* Label */}
              <span className="text-xs font-bold tracking-wide uppercase text-white dark:text-white">
                {row.original.separatorLabel}
              </span>
            </div>
          </Td>
        </Tr>
      );
    }
    
    return (
    <Tr
      key={row.id}
      ref={ref}
      data-index={dataIndex}
      title={getRowTitle?.(row.original) || undefined}
      className={clsx(
        "dark:border-b-dark-500 border-y border-transparent border-b-gray-200 cursor-pointer transition-colors hover:bg-slate-50/50 dark:hover:bg-dark-800/50",
        getRowClassName?.(row.original)
      )}
      onClick={() => {
        onRowClick(row.original);
        if (renderDetailRow && expandOnRowClick) {
          row.toggleExpanded();
        }
      }}
      onContextMenu={(e) => {
        if (onRowContextMenu) {
          if (window.innerWidth >= 768) {
            e.preventDefault();
            onRowContextMenu(e, row.original);
          }
        } else {
          triggerRowContextMenu(e, row.original);
        }
      }}
    >
      {row
        .getVisibleCells()
        .filter((cell) => !cell.column.columnDef.isHiddenColumn)
        .map((cell) => (
          <Td
            key={cell.id}
            className={clsx(
              "relative bg-white",
              cardSkin === "shadow"
                ? "dark:bg-dark-700"
                : "dark:bg-dark-900",
              cell.column.getCanPin() && cell.column.getIsPinned() && "sticky z-20",
              row.getIsSelected() && "!bg-slate-100 dark:!bg-dark-800",
              row.getIsSelected() && "first:ltr:border-l-4 first:rtl:border-r-4 first:border-l-primary-500 first:border-r-primary-500",
              cell.column.columnDef.meta?.className,
              cell.column.id === "select" && "cursor-pointer"
            )}
            onClick={(e) => {
              if (cell.column.id === "select") {
                e.stopPropagation();
                if (e.target.tagName !== "INPUT" && row.getCanSelect()) {
                  row.toggleSelected(!row.getIsSelected());
                }
              }
            }}
            style={{
              ...getPinnedStyles(cell.column),
              width: useFixedLayout ? cell.column.getSize() : undefined,
              minWidth: cell.column.columnDef.minSize || undefined,
            }}
          >
            {cell.column.getIsPinned() && (
              <div
                className={clsx(
                  "dark:border-dark-500 pointer-events-none absolute inset-0 border-gray-200",
                  cell.column.getIsPinned() === "left"
                    ? "ltr:border-r rtl:border-l"
                    : "ltr:border-l rtl:border-r"
                )}
              />
            )}
            {flexRender(
              cell.column.columnDef.cell,
              cell.getContext()
            )}
          </Td>
        ))}
    </Tr>
    );
  };

  const renderDetailRowContent = (row, { ref, dataIndex } = {}) => {
    const content = renderDetailRow(row.original, totalVisibleColumns, {
      table,
      enableSorting: tableSettings.enableSorting,
    });
    if (isValidElement(content) && (content.type === "tr" || content.type === Tr)) {
      return cloneElement(content, { ref, "data-index": dataIndex });
    }
    return (
      <Tr key={`${row.id}__detail`} ref={ref} data-index={dataIndex}>
        <Td colSpan={totalVisibleColumns} className="p-0">
          {content}
        </Td>
      </Tr>
    );
  };

  const tableContent = (
    <div
      className={clsx(
        "flex h-full w-full flex-col min-h-0"
      )}
    >
      {activeEnableFullScreen && (
        <style>
          {`
            .app-header { display: none !important; }
          `}
        </style>
      )}
      {renderTopActions && renderTopActions({ table })}
      {showToolbar && (
        <Toolbar
          table={table}
          title={title}
          showSearch={showSearch}
          showStatusFilter={showStatusFilter}
          showActionButtons={showActionButtons}
          showColumnConfig={showColumnConfig}
          onPrint={onPrint}
          onExportPDF={onExportPDF}
          onExportCSV={onExportCSV}
          headerActions={headerActions}
          searchPlaceholder={searchPlaceholder}
          disablePortal={disableToolbarPortal}
        />
      )}
      {renderBelowToolbar && renderBelowToolbar({ table })}
      <div
        className={clsx(
          "transition-content flex grow flex-col min-h-0",
          activeEnableFullScreen ? "overflow-hidden pt-2" : "px-0 overflow-hidden"
        )}
      >
        <Card
          className={clsx(
            "relative flex grow flex-col min-h-0",
            loading && "min-h-[200px]",
            "overflow-hidden",
            "flex-1 border border-gray-300 dark:border-dark-600 shadow-sm bg-white dark:bg-dark-800 transition-all",
            !activeEnableFullScreen && "!rounded-2xl"
          )}
        >
          {loading && (!data || data.length === 0) ? (
            <TableSkeleton
              rows={Math.max(pagination?.limit || 5, 5)}
              columns={simpleColumns.length <= 1 ? 6 : simpleColumns.length}
              dense={tableSettings.enableRowDense}
              colDense={tableSettings.enableColDense}
            />
          ) : (
            <>
              {loading && data && data.length > 0 && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 dark:bg-dark-900/50 backdrop-blur-[1px]">
                  <div className="h-8 w-8 rounded-full border-4 border-primary-500 border-t-transparent animate-spin"></div>
                </div>
              )}
              {rows.length > 0 && (
                <div
                  ref={tableRef}
                  className={clsx(
                    "table-wrapper min-w-full overflow-auto custom-scrollbar",
                    rows.length > 0 && "grow"
                  )}
                >
                  <Table
                    hoverable
                    dense={dense !== undefined ? dense : tableSettings.enableRowDense}
                    colDense={colDense !== undefined ? colDense : tableSettings.enableColDense}
                    sticky
                    className="w-full text-left rtl:text-right"
                    style={{
                      tableLayout: useFixedLayout ? "fixed" : undefined,
                      width: useFixedLayout ? table.getTotalSize() : undefined,
                      minWidth: "100%",
                    }}
                  >
                    <THead className="!z-30 bg-gray-200 dark:bg-dark-700">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id} className="bg-gray-200 dark:bg-dark-700">
                          {headerGroup.headers
                            .filter(
                              (header) => !header.column.columnDef.isHiddenColumn
                            )
                            .map((header) => (
                              <Th
                                key={header.id}
                                data-column-id={header.column.id}
                                colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                                rowSpan={header.rowSpan > 1 ? header.rowSpan : undefined}
                                draggable={
                                  header.column.id !== "select" &&
                                  header.column.id !== "actions" &&
                                  hoveredResizerId !== header.column.id &&
                                  !header.column.getIsResizing()
                                }
                                onDragStart={(e) => handleDragStart(e, header.column.id)}
                                onDragOver={(e) => handleDragOver(e, header.column.id)}
                                onDrop={(e) => handleDrop(e, header.column.id)}
                                onDragEnd={handleDragEnd}
                                className={clsx(
                                  "dark:bg-dark-700 dark:text-dark-100 bg-gray-200 font-semibold text-gray-800 uppercase select-none transition-colors !border-r-2 !border-r-white dark:!border-r-dark-900 last:!border-r-0 whitespace-nowrap",
                                  header.column.id !== "select" && header.column.id !== "actions" && "cursor-grab active:cursor-grabbing",
                                  header.column.id === draggedColumnId && "opacity-40 border-dashed border border-primary-500",
                                  header.column.getCanPin() && header.column.getIsPinned() && "sticky z-40",
                                  header.column.columnDef.meta?.headerClassName,
                                  header.column.id === "select" && "cursor-pointer"
                                )}
                                onClick={(e) => {
                                  if (header.column.id === "select") {
                                    e.stopPropagation();
                                    if (e.target.tagName !== "INPUT") {
                                      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
                                    }
                                  }
                                }}
                                style={{
                                  ...getPinnedStyles(header.column, true),
                                  width: useFixedLayout ? header.getSize() : undefined,
                                  minWidth: header.column.columnDef.minSize || undefined,
                                }}
                              >
                                {header.column.getCanSort() ? (
                                  <div
                                    className={clsx(
                                      "flex cursor-pointer items-center space-x-2 select-none whitespace-nowrap w-full",
                                      header.column.columnDef.meta?.headerClassName?.includes("text-center") && "justify-center",
                                      header.column.columnDef.meta?.headerClassName?.includes("text-right") && "justify-end",
                                      !header.column.columnDef.meta?.headerClassName?.includes("text-center") && !header.column.columnDef.meta?.headerClassName?.includes("text-right") && "justify-start"
                                    )}
                                    onClick={header.column.getToggleSortingHandler()}
                                  >
                                    <span className="whitespace-nowrap font-semibold">
                                      {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext()
                                        )}
                                    </span>
                                    <div className="shrink-0 flex items-center justify-center">
                                      <TableSortIcon
                                        sorted={header.column.getIsSorted()}
                                      />
                                    </div>
                                  </div>
                                ) : header.isPlaceholder ? null : (
                                  <div className="whitespace-nowrap">
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                  </div>
                                )}
                                {header.column.getCanResize() && (
                                  <div
                                    onMouseDown={header.getResizeHandler()}
                                    onTouchStart={header.getResizeHandler()}
                                    onMouseEnter={() => setHoveredResizerId(header.column.id)}
                                    onMouseLeave={() => setHoveredResizerId(null)}
                                    className={clsx(
                                      "table-resizer",
                                      header.column.getIsResizing() && "is-resizing"
                                    )}
                                  />
                                )}
                              </Th>
                            ))}
                        </Tr>
                      ))}
                    </THead>
                    <TBody>
                      {shouldVirtualize ? (
                        <>
                          {activeSeparatorItem && renderDataRow(activeSeparatorItem.row)}
                          {adjustedPaddingStart > 0 && (
                            <Tr aria-hidden="true">
                              <Td
                                colSpan={totalVisibleColumns}
                                style={{ height: adjustedPaddingStart, padding: 0, border: 0 }}
                              />
                            </Tr>
                          )}
                          {virtualItems.map((virtualItem) => {
                            const item = flatItems[virtualItem.index];
                            if (!item) return null;
                            const measureProps = { ref: rowVirtualizer.measureElement, dataIndex: virtualItem.index };
                            return item.type === "row"
                              ? renderDataRow(item.row, measureProps)
                              : renderDetailRowContent(item.row, measureProps);
                          })}
                          {virtualPaddingEnd > 0 && (
                            <Tr aria-hidden="true">
                              <Td
                                colSpan={totalVisibleColumns}
                                style={{ height: virtualPaddingEnd, padding: 0, border: 0 }}
                              />
                            </Tr>
                          )}
                        </>
                      ) : (
                        rows.map((row) => (
                          <Fragment key={row.id}>
                            {renderDataRow(row)}
                            {row.getIsExpanded() && renderDetailRow && renderDetailRowContent(row)}
                          </Fragment>
                        ))
                      )}
                    </TBody>
                  </Table>
                </div>
              )}

              {rows.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center grow animate-fade-in">
                  {/* Floating Glow Circle */}
                  <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-tr from-slate-50 to-slate-100/50 dark:from-dark-800 dark:to-dark-700/50 border border-slate-200/60 dark:border-dark-600/40 shadow-sm mb-6">
                    {/* Subtle Background Glow */}
                    <div className="absolute inset-0 bg-primary/10 rounded-2xl blur-xl opacity-0 dark:opacity-40 animate-pulse" />

                    <svg
                      className="w-10 h-10 text-slate-400 dark:text-dark-300 relative z-10"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25m-2.25-2.25l-2.25 2.25m2.25-2.25l2.25-2.25M3.75 5.625c0-.621.504-1.125 1.125-1.125h14.25c.621 0 1.125.504 1.125 1.125v1.875c0 .621-.504 1.125-1.125 1.125H4.875C4.254 8.625 3.75 8.121 3.75 7.5V5.625z"
                      />
                    </svg>
                  </div>

                  <h3 className="text-base font-semibold text-slate-800 dark:text-dark-50 mb-2">
                    {noDataMessage}
                  </h3>

                  <p className="text-sm text-slate-500 dark:text-dark-400 max-w-sm mb-6 leading-relaxed">
                    {noDataSubtitle || (activeGlobalFilter
                      ? `We couldn't find any matches for "${activeGlobalFilter}". Try checking your spelling or using different keywords.`
                      : "No records are currently available in this view.")
                    }
                  </p>

                  {activeGlobalFilter && (
                    <button
                      onClick={() => activeOnGlobalFilterChange("")}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs md:text-sm font-bold text-white shadow-sm hover:bg-primary-600 focus:outline-none transition-all hover:scale-105 active:scale-95 mb-2"
                    >
                      Clear search query
                    </button>
                  )}
                  {!activeGlobalFilter && emptyStateAction}
                </div>
              )}
              {renderSelectedActions ? (
                renderSelectedActions({ table })
              ) : (
                <></>
              )}
              {showPagination && (isServerSide ? currentTotal > 0 : table.getCoreRowModel().rows.length > 0) && (
                <div
                  className={clsx(
                    "px-4 pb-2 sm:px-3 sm:pt-2",
                    tableSettings.enableFullScreen && "dark:bg-dark-800 bg-gray-50",
                    !(
                      table.getIsSomeRowsSelected() ||
                      table.getIsAllRowsSelected()
                    ) && "pt-2"
                  )}
                >
                  <PaginationSection table={table} compact={compactPagination} />
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );

  return (
    <div className="transition-content w-full h-full pb-3 flex flex-col overflow-hidden">
      <GlobalTableContextMenu />
      {tableContent}
    </div>
  );
}
